import React, { useRef, useState } from "react";
import { Controller, useForm, useFormContext } from "react-hook-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Ticket } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
} from "@/components/ui/Select";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup";
import { TicketResponse, useGenerateUserTicketFromServiceMutation, useGetAllDepartmentServiceByIdQuery } from "@/services/SelfServiceKiosk";
import useTableData from "@/components/ui/table/DataTableLogic";
import CustomAlertDialog from "@/components/custom-component/CustomAlertDialog";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { useToast } from "@/components/ui/UseToast";
import { format } from "date-fns";
import TicketAvailabilityCalendar from "../components/TicketAvailabilityCalendar";

export interface FormValues { title: string; description?: string; date: string; time: string; service_tag: string }
const initialValue = {
  limit: 8,
  offset: 0,
  service_name:""
}

function TicketForm({
  departmentId,
  open,
  onClose,
  onTicketCreated,
}: {
  departmentId: number | undefined;
  open: boolean;
  onClose: () => void;
  onTicketCreated?: (ticket: TicketResponse) => void;
}) {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const { toast } = useToast();
  const form = useFormContext<FormValues>();
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const { control, handleSubmit, setValue, watch } = useFormContext<FormValues>();
  const [scheduleType, setScheduleType] = useState<'appointment' | 'now'>('now');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [ticketResponse, SetTicketResponse] = useState<TicketResponse>();

  const { searchCriteria, query, setSearchCriteria } = useTableData(initialValue);

  const {
    data: servicesData,
    isLoading,
    refetch,
    error,
  } = useGetAllDepartmentServiceByIdQuery({
    department_id: departmentId!,
    query: `${query}&ignore_schedule=true&is_public=true`,
  }, {
    skip: !departmentId
  });
  const [generateTicket,{isLoading:isSubmitting}] = useGenerateUserTicketFromServiceMutation();
  const services = servicesData?.data || [];
  const hasTags = services.some((service) => (service.tags?.length ?? 0) > 0);
  const serviceTagValue = watch("service_tag");

  const selectedServiceForCalendar = React.useMemo(() => {
    if (!serviceTagValue) return null;
    const [serviceId] = serviceTagValue.split(",");
    const selectedService = services.find(
      (service) => service.id === parseInt(serviceId)
    );
    if (!selectedService) return null;

    // TicketAvailabilityCalendar expects `Service` from `ServiceCard` (shape: `{ id, schedule }`).
    // TicketForm services currently use `schedules`, so adapt it.
    return selectedService
  }, [serviceTagValue, services]);

  const onSubmit = async (data: FormValues) => {
    if (!userInfo?.id) return;
    if (!data.date) data.date = format(new Date(), "yyyy-MM-dd");
    if (!data.time) data.time = format(new Date(), "HH:mm");
    const formattedTime = data.time.includes(":") ? `${data.time}:00` : data.time;
    try {
      // Fetch selected service schedule
      console.log("This is the date i go", data);
      const [serviceId, tag] = data.service_tag.split(',');
      const selectedService = servicesData?.data.find(service => service.id === parseInt(serviceId));
      if (!selectedService) {
        toast({
          variant: "destructive",
          title: "Sub Umoor Not Found",
          description: "Could not find the selected Sub Umoor schedule.",
        });
        return;
      }

      // Convert selected date to a Date object
      const selectedDate = new Date(data.date);
      const selectedDay = selectedDate.toLocaleString("en-US", {
        weekday: "long",
      });

      // Find the service schedule for the selected day
      const serviceSchedule = selectedService.schedules.find(
        (s) => s.day === selectedDay.toLowerCase()
      );

      if (!serviceSchedule) {
        toast({
          variant: "destructive",
          title: "Sub Umoor Unavailable",
          description: "This Sub Umoor is not available on the selected date.",
        });
        return;
      }

      // Convert opening, closing, and ticket end times to Date objects
      const [openHour, openMin] = serviceSchedule.opening_time
        .split(":")
        .map(Number);
      const [closeHour, closeMin] = serviceSchedule.closing_time
        .split(":")
        .map(Number);
      const [ticketEndHour, ticketEndMin] = serviceSchedule.ticket_end_time
        .split(":")
        .map(Number);

      const openingTime = new Date(selectedDate);
      openingTime.setHours(openHour, openMin, 0, 0);

      const closingTime = new Date(selectedDate);
      closingTime.setHours(closeHour, closeMin, 0, 0);

      const ticketEndTime = new Date(selectedDate);
      ticketEndTime.setHours(ticketEndHour, ticketEndMin, 0, 0);

      // Convert user input time to Date object
      const [userHour, userMin] = data.time.split(":").map(Number);
      const userSelectedTime = new Date(selectedDate);
      userSelectedTime.setHours(userHour, userMin, 0, 0);

      const today = new Date();
      const isSameDay = selectedDate.toDateString() === today.toDateString();

      // ✅ Case 1: User selects a time outside service hours
      if (userSelectedTime < openingTime || userSelectedTime > ticketEndTime) {
        toast({
          variant: "destructive",
          title: "Invalid Time",
          description: `Selected time must be within Sub Umoor hours: ${openHour % 12 || 12}:${String(openMin).padStart(2, "0")} ${openHour >= 12 ? "PM" : "AM"} - ${ticketEndHour % 12 || 12}:${String(ticketEndMin).padStart(2, "0")} ${ticketEndHour >= 12 ? "PM" : "AM"},
          or please book an appointment for future date.`,
        });
        return;
      }

      // ✅ Case 2: Same-day ticket after ticket end time → ❌ Not allowed
      if (isSameDay && userSelectedTime > ticketEndTime) {
        toast({
          variant: "destructive",
          title: "Sub Umoor Closed for the Day",
          description:
            "Sub Umoor is closed for the day, please book an appointment for a later date.",
        });
        return;
      }

      // ✅ Case 3: Future booking outside service schedule → ❌ Not allowed
      if (!isSameDay && !serviceSchedule) {
        toast({
          variant: "destructive",
          title: "Sub Umoor Unavailable",
          description:
            "Sub Umoor is not available on the selected date. Please choose another date.",
        });
        return;
      }

      // Call API to generate ticket
      await generateTicket({
        title: tag,
        description: data.description || "",
        date: data.date,
        time: `${formattedTime}`,
        tag_id: +serviceId,
        user_id: parseInt(userInfo.id),
        eta:40,
        status: "NEW",
        is_admin: false,
        path: '/' + selectedService.path.join('/'),
        ...(selectedService.event_id ? {event_id: selectedService.event_id} : {})
      })
        .unwrap()
        .then(async (res) => {
          if (res?.detail === "You already have a pending ticket for today") {
            setIsModalOpen(false);
            toast({
              variant: "destructive",
              title: "Ticket Creation Failed",
              description: "You already have a pending ticket for today.",
            });
            return;
          }

          onClose();
          onTicketCreated?.(res);
          SetTicketResponse(res);
          setIsModalOpen(true);
          // Format closing time properly
          const [resHour, resMin] = res?.closing_time.split(":").map(Number);
          const closingTimeFormatted = `${resHour % 12 || 12}:${String(resMin).padStart(2, "0")} ${resHour >= 12 ? "PM" : "AM"}`;
        })
        .catch((err) => {
          setIsModalOpen(false);
          toast({
            variant: "destructive",
            title: "Failed to create Ticket",
            description:
              err && "data" in err && typeof err.data === "object"
                ? (err.data as any).detail ||
                  (err.data as any).message ||
                  "Something went wrong"
                : "Something went wrong",
          });
        });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error Generating Ticket",
        description:
          error?.data?.detail || error?.data?.message || "Something went wrong",
      });
    }
  };

  const renderBody = () => {
    const closingTimeParts = ticketResponse?.closing_time
      ?.split(":")
      .map(Number) || [0, 0]; // Extract hour and minute, default to [0, 0]
    const [closingHour, closingMinute] = closingTimeParts; // Extract hour and minute

    const closingTimeAdjusted = closingHour % 12 || 12; // Convert to 12-hour format, handling 12 AM/PM case
    const period = closingHour >= 12 ? "PM" : "AM"; // Determine AM/PM

    const formattedClosingTime = `${closingTimeAdjusted}:${String(closingMinute).padStart(2, "0")} ${period}`;

    return (
      <div className="text-center bg-white rounded-lg h-[10rem] w-full max-w-xs relative flex flex-col justify-start p-2 items-center overflow-hidden sm:max-h-[14rem] sm:overflow-y-auto">
        {/* Service Name */}
        <h2 className="text-[1.2rem] capitalize text-gray-600 font-semibold mt-0 break-words">
          {ticketResponse?.service_name}
        </h2>

        <p className="text-black text-[1.2rem] font-bold break-words">
          {ticketResponse?.assigned_number}
        </p>
        {/* Service Code */}
        <p className="text-black text-[1.2rem] font-bold break-words">
          {ticketResponse?.ticket_number}
        </p>

        {/* Information 
        <div className="text-gray-600 text-center text-sm">
          <p className="font-medium">Counter</p>
          <p className="text-black font-bold text-[1rem] leading-tight break-words whitespace-normal max-w-[12rem] mx-auto">
            {ticketResponse?.counter_code}
          </p>
        </div>*/}

        {/* Closing Time (positioned at the bottom center) */}
        <div className="absolute bottom-[14px] left-1/2 transform -translate-x-1/2 w-full">
          <p className="text-[0.8rem] text-gray-600 font-normal">
            Sub Umoor Closing Time
          </p>
          <p className="text-[1.1rem] text-black font-semibold break-words">
            {formattedClosingTime ?? ticketResponse?.closing_time}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={onClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogDescription>
              <div className="flex flex-row items-center justify-start gap-4">
                <Ticket className="size-14" />
                <AlertDialogTitle className="text-sm md:text-lg font-medium">
                  Please Provide the following detail for appointment
                </AlertDialogTitle>
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="mt-4 space-y-2 w-full"
              >
                <div className="space-y-2 w-full">
                  <ToggleGroup 
                    type="single" 
                    value={scheduleType} 
                    onValueChange={(value) => value && setScheduleType(value as 'now' | 'appointment')} 
                    className="w-full justify-start"
                  >
                    <ToggleGroupItem value="now" className="flex-1 data-[state=on]:bg-primary data-[state=on]:text-white">Now</ToggleGroupItem>
                    <ToggleGroupItem value="appointment" className="flex-1 data-[state=on]:bg-primary data-[state=on]:text-white">Appointment</ToggleGroupItem>
                  </ToggleGroup>

                  <Label htmlFor="title" className="block text-left">
                    Title
                  </Label>
                  <div>
                    <Controller
                      name="service_tag"
                      rules={{
                        required: "Title is required",
                        maxLength: {
                          value: 50,
                          message: "Title cannot exceed 50 characters",
                        },
                      }}
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                        <Select
                          value={field.value || ""}
                          onValueChange={(value) => {
                            if (!value) return;
                            field.onChange(value)
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>

                          <SelectContent>
                            {isLoading ? (
                              <SelectItem value="loading-tags" disabled>
                                Loading tags...
                              </SelectItem>
                            ) : error || services.length <= 0 || !hasTags ? (
                              <SelectItem value="no-tags-found" disabled>
                                No tags found
                              </SelectItem>
                            ) : (
                              services.map((service) =>
                                (service.tags || []).map((tag) => (
                                  <SelectItem key={`${service.id}-${tag}`} value={`${service.id},${tag}`}>
                                    <span className="capitalize">{service.name} - {tag}</span>
                                  </SelectItem>
                                ))
                              )
                            )}
                          </SelectContent>
                        </Select>
                        {fieldState.error && (
                          <p className="text-red-500 text-xs text-left">
                            {fieldState.error.message}
                          </p>
                        )}
                        </>
                      )}
                    />
                  </div>
                </div>

                {/* Description Input */}
                <div className="space-y-2 w-full">
                  <label
                    htmlFor="description"
                    className="block text-sm font-semibold text-start"
                  >
                    Description
                  </label>
                  <Controller
                    control={control}
                    name="description"
                    rules={{
                      maxLength: {
                        value: 350,
                        message: "description cannot exceed 350 characters",
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <>
                      <textarea
                        {...field}
                        rows={4}
                        placeholder="Enter description"
                        className="w-full border rounded p-2"
                      />
                      {fieldState.error && (
                        <p className="text-red-500 text-xs text-start">
                          {fieldState.error.message}
                        </p>
                      )}
                      </>
                    )}
                  />
                </div>

                {scheduleType === "appointment" && (
                <>
                <div
                  className="space-y-2 cursor-pointer"
                >
                  <label
                    htmlFor="date"
                    className="block text-sm font-semibold text-start"
                  >
                    Date
                  </label>
                  <Controller
                    control={control}
                    name="date"
                    rules={{ required: "Date is required" }}
                    render={({ field, fieldState }) => (
                      <>
                      <TicketAvailabilityCalendar
                        value={field.value}
                        onChange={field.onChange}
                        service={selectedServiceForCalendar}
                      />
                      {fieldState.error && (
                        <p className="text-red-500 text-xs text-start">
                          {fieldState.error.message}
                        </p>
                      )}
                      </>
                    )}
                  />
                </div>
                <div className="space-y-2 w-full">
                  <label
                    htmlFor="date"
                    className="block text-sm font-semibold text-start"
                  >
                    Time
                  </label>
                  <Controller
                    control={control}
                    name="time"
                    rules={{ required: "Time is required" }}
                    render={({ field, fieldState }) => (
                      <>
                      <Input
                        {...field}
                        type="time"
                        id="time"
                        aria-label="Choose time"
                        className="w-full"
                      />
                      {fieldState.error && (
                        <p className="text-red-500 text-xs text-start">
                          {" "}
                          {fieldState.error.message}
                        </p>
                      )}
                      </>
                    )}
                  />
                </div>
                </>
                )}

                {/* Buttons */}
                <div className="flex justify-end items-center gap-3 mt-4">
                  <AlertDialogCancel
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="w-[100px] border border-primary font-semibold"
                  >
                    <i className="fa fa-xmark text-base px-1"></i> No
                  </AlertDialogCancel>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-[140px] bg-primary text-black font-semibold"
                  >
                    <i className="fa-regular fa-floppy-disk text-base px-1"></i>{" "}
                    {isSubmitting ? "Generating..." : "Yes"}
                  </Button>
                </div>
              </form>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
      <CustomAlertDialog
        body={renderBody}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        isBodyAllow={true}
        title="Sub Umoor Details"
        description="More details about the selected Sub Umoor"
        onConfirm={() => {}}
        className="w-[20rem] bg-slate-600"
      />
    </>
  )
}

export default TicketForm;
